import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Share2, ArrowRight, Sparkles, Users, Copy, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { compareProfiles } from "../lib/api";

// ---------------------------------------------------------------------------
// Compare — /compare/:handleA/:handleB. Public, no auth needed. Shows a
// side-by-side persona panel + composite match score + shared favourites +
// a downloadable share card (same html-to-image tech as Persona/Wrapped).
// ---------------------------------------------------------------------------

export default function Compare() {
  const { handleA, handleB } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    compareProfiles(handleA, handleB)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Couldn't load comparison");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [handleA, handleB]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#050505" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `otaku-compare-${data.a.handle}-vs-${data.b.handle}.png`;
      a.click();
      toast.success("Card downloaded");
    } catch (err) {
      console.warn(err);
      toast.error("Could not export card");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[900px] px-6 py-24 text-center md:px-10" data-testid="compare-error">
        <h1 className="font-display text-5xl uppercase text-white md:text-6xl">Can&apos;t compare</h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
          {error || "One or both handles are private or don't exist."}
        </p>
        <Link
          to="/"
          className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
        >
          Back to home
        </Link>
      </div>
    );
  }

  const { a, b, match } = data;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
      <div className="mb-8 flex flex-col gap-4 border-b border-white/10 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Persona Match</p>
          <h1 className="mt-2 font-display text-4xl uppercase leading-[0.9] tracking-tight text-white md:text-6xl">
            @{a.handle} <span className="text-neutral-600">×</span> @{b.handle}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copyLink}
            data-testid="compare-copy-link"
            className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
          >
            <Copy className="h-3 w-3" /> Copy Link
          </button>
          <button
            onClick={downloadCard}
            data-testid="compare-download"
            className="inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
          >
            <Download className="h-3 w-3" /> Download Card
          </button>
        </div>
      </div>

      {/* Downloadable card wraps score + persona snapshot */}
      <div ref={cardRef} className="bg-[#050505]" data-testid="compare-card">
        <MatchHero a={a} b={b} match={match} />

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <PersonaPanel who="You" user={a} testid="compare-panel-a" />
          <PersonaPanel who="Them" user={b} testid="compare-panel-b" />
        </div>

        <ComponentsRow match={match} />
      </div>

      <SharedGenres match={match} />
      <SharedFavourites match={match} a={a} b={b} />

      <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-8">
        <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          Otaku Index · Persona Match
        </p>
        <div className="flex gap-2">
          <Link to={`/u/${a.handle}`} className="border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
            @{a.handle} profile
          </Link>
          <Link to={`/u/${b.handle}`} className="border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
            @{b.handle} profile
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function MatchHero({ a, b, match }) {
  const bar = Math.max(2, match.score);
  return (
    <div className="border border-white/10 bg-white/[0.02] p-6 md:p-10" data-testid="compare-hero">
      <div className="flex items-center justify-between gap-6">
        <UserBadge user={a} align="left" />
        <div className="flex flex-col items-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-neutral-500">Match</div>
          <div
            className="font-display text-6xl uppercase leading-none tracking-tight text-white md:text-8xl"
            data-testid="compare-score"
          >
            {match.score}%
          </div>
        </div>
        <UserBadge user={b} align="right" />
      </div>

      <div className="mt-6 h-2 w-full border border-white/10 bg-black">
        <div
          className="h-full bg-[#FF3B30]"
          style={{ width: `${bar}%` }}
          data-testid="compare-score-bar"
        />
      </div>
      <p className="mt-4 max-w-2xl text-sm text-neutral-300 md:text-base" data-testid="compare-verdict">
        {match.verdict}
      </p>
    </div>
  );
}

function UserBadge({ user, align }) {
  const initial = (user.display_name || user.handle || "?").charAt(0).toUpperCase();
  return (
    <div className={`flex items-center gap-3 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      {user.picture ? (
        <img src={user.picture} alt={user.display_name} crossOrigin="anonymous"
          className="h-16 w-16 border border-white/15 object-cover md:h-20 md:w-20" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center border border-white/15 bg-[#111] font-display text-3xl uppercase text-white md:h-20 md:w-20">
          {initial}
        </div>
      )}
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">@{user.handle}</p>
        <p className="mt-1 font-display text-xl uppercase leading-tight tracking-tight text-white md:text-2xl">
          {user.display_name}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PersonaPanel({ who, user, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">// {who} · @{user.handle}</p>
      <h3 className="mt-3 font-display text-3xl uppercase leading-tight tracking-tight text-white">
        {user.personality_type || "—"}
      </h3>
      {user.core_description && (
        <p className="mt-2 text-sm text-neutral-400">{user.core_description}</p>
      )}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <MiniStat label="Top Genre" value={user.top_genre || "—"} />
        <MiniStat label="Anime" value={user.total_anime ?? 0} />
        <MiniStat label="Avg Rating" value={user.average_rating_given ?? "—"} />
      </div>
      {(user.secondary_genres || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {user.secondary_genres.map((g) => (
            <span key={g} className="border border-white/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neutral-400">
              {g}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-white/10 bg-black/40 p-2">
      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 font-display text-lg uppercase leading-none tracking-tight text-white">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ComponentsRow({ match }) {
  const c = match.components;
  return (
    <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="compare-components">
      <ComponentBar label="Genre Similarity" value={c.genre_similarity} />
      <ComponentBar label="Shared Titles" value={c.shared_titles_ratio} />
      <ComponentBar label="Rating Agreement" value={c.rating_agreement} />
    </div>
  );
}

function ComponentBar({ label, value }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</p>
        <p className="font-mono text-[11px] text-white">{value}%</p>
      </div>
      <div className="mt-3 h-1.5 w-full border border-white/10 bg-black">
        <div className="h-full bg-white" style={{ width: `${Math.max(2, value)}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SharedGenres({ match }) {
  if (!match.shared_genres || match.shared_genres.length === 0) return null;
  return (
    <div className="mt-10" data-testid="compare-shared-genres">
      <h2 className="border-b border-white/10 pb-3 font-display text-2xl uppercase tracking-widest text-white">
        Shared Vibes
      </h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {match.shared_genres.map((g) => (
          <span key={g} className="border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white">
            {g}
          </span>
        ))}
      </div>
    </div>
  );
}

function SharedFavourites({ match, a, b }) {
  if (!match.shared_favourites || match.shared_favourites.length === 0) {
    return (
      <div className="mt-10 border border-dashed border-white/15 p-6 text-center" data-testid="compare-shared-empty">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">// Shared Titles</p>
        <p className="mt-3 text-sm text-neutral-400">
          No overlap yet — a great excuse to swap recs.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-10" data-testid="compare-shared-list">
      <h2 className="border-b border-white/10 pb-3 font-display text-2xl uppercase tracking-widest text-white">
        You Both Watched
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
        {match.shared_favourites.map((sf) => (
          <Link
            key={sf.mal_id}
            to={`/anime/${sf.mal_id}`}
            className="group border border-white/10 bg-[#0a0a0a] hover:border-[#FF3B30]"
            data-testid={`compare-shared-${sf.mal_id}`}
          >
            <div className="aspect-[2/3] w-full overflow-hidden bg-[#111]">
              {sf.image_url ? (
                <img src={sf.image_url} alt={sf.title} loading="lazy" crossOrigin="anonymous"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : null}
            </div>
            <div className="p-2">
              <p className="line-clamp-2 min-h-[2rem] font-display text-xs uppercase leading-tight tracking-wide text-white">
                {sf.title}
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-neutral-400">
                @{a.handle}: {sf.your_rating ?? "—"} · @{b.handle}: {sf.their_rating ?? "—"}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
